/**
 * Oversikt → Dagen (manager redesign, 2026-10-09): who is working and what needs a look today, as a
 * list. No calendar here; the planning timeline is Vaktplan's day view ("Åpne dagsplan"). Planned
 * time and recorded time are separate columns; presence and review are separate (day.ts).
 */
import { errorText } from '@/api/client';
import {
  type ApiAbsence,
  type ApiShift,
  type ApiUser,
  useAbsenceTypes,
  useAbsences,
  useAvailabilities,
  useShiftTypes,
  useShiftWrites,
  useTaskStatus,
  useTeams,
  useUsers,
  useWorkTimeChanges,
} from '@/api/data';
import { useLabour } from '@/api/labour';
import { invalidateShifts } from '@/api/shifts';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { breakPolicy, dayEntries, hours, oslo, osloISO, rulePerson, tenantRules } from '@/rules';
import { modeOf } from '@/rules/breakSetting';
import { ratesOf } from '@/rules/labour';
import { useTenant } from '@/shell/tenant';
import { Button, Empty, Skeleton, toast } from '@/ui';
import { PersonLabel } from '@/ui/PersonLabel';
import { NO_VALUE, avTone, fmtDs, fullName, hhmm, num, shiftSpan, typeName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { DetailPanel, Explain, Fact, Status, initialsOf } from '@/ui/page';
import { useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { addDays, dow } from '@timefly/core';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { events, clock, csvOf, estimate, perHour, span, unscheduled } from '../dagen/today';
import { type Kind, kindOfAbsence } from '../fravaer/AbsenceRange';
import { taskDue } from '../hjem/cards';
import { FirstPlanHint } from '../vaktplan/FirstPlan';
import { type DraftShift, ShiftDrawer, draftOf } from '../vaktplan/ShiftDrawer';
import { toVaktplan } from '../vaktplan/link';
import { useShiftLock } from '../vaktplan/lock';
import { availOn } from '../vaktplan/model';
import { type HourGap, gapText, hourGaps, pieces } from '../vaktplan/staffing';
import { Coverage, gapSpan } from './Coverage';
import { dayTitle } from './Oversikt';
import { ReadNotice } from './ReadNotice';
import { absenceSpan, staffWork } from './cover';
import { type DayRow, FILTERS, type Filter, counts, freeShown, matches, showsPause, tasksLeft } from './day';
import { overviewLinks } from './navigation';

const kr = (n: number) => Math.round(n).toLocaleString('nb-NO');
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? T(one) : T(many)}`;

export function DagenTab({
  d,
  today,
  now,
  rows,
  rota,
  shiftsQ,
  dayShifts,
}: {
  d: string;
  today: string;
  now: number;
  rows: DayRow[];
  rota: boolean;
  shiftsQ: UseQueryResult<{ shifts: ApiShift[] }>;
  dayShifts: ApiShift[];
}) {
  const can = useCan();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const { tenant } = useTenant();
  // a shift keeps the mode it was punched in under; before the punch the company's applies
  const breakMode = modeOf(tenant);
  const labour = useLabour();
  const usersQ = useUsers();
  const teamsQ = useTeams();
  const absQ = useAbsences();
  const absTypesQ = useAbsenceTypes(can('fravaer.se'));
  const availQ = useAvailabilities();
  const typesQ = useShiftTypes();
  const w = useShiftWrites();
  const lock = useShiftLock(async () => {
    const r = await shiftsQ.refetch();
    if (r.status === 'error') throw r.error;
    return r.data ?? null;
  });
  const taskStatus = useTaskStatus();
  const seesHistory = can('timeliste.se');
  const historyQ = useWorkTimeChanges(
    { from: osloISO(d, '00:00'), to: osloISO(addDays(d, 1), '00:00') },
    seesHistory,
  );
  const [draft, setDraft] = useState<DraftShift | null>(null);
  // Rett stempling opens the editor on the punches, not on the plan (#273 review S2-2)
  const [onPunch, setOnPunch] = useState(false);
  // cost is shown by default to someone who may see it (#94 point 7); the toggle hides it
  const [showCost, setShowCost] = useState(true);
  const [freeQ, setFreeQ] = useState('');
  const [freeAll, setFreeAll] = useState(false);
  const [laterOpen, setLaterOpen] = useState(false);
  const [doneTask, setDoneTask] = useState<number | null>(null);

  const users = usersQ.data ?? [];
  const types = typesQ.data ?? [];
  const team = can('vaktplan.se') ? Number(sp.get('lag')) || 0 : 0;
  const listKnown = shiftsQ.data !== undefined && (!team || usersQ.data !== undefined);
  const staffingKnown = shiftsQ.data !== undefined && labour.settings !== undefined;
  const staffingReady = staffingKnown && !shiftsQ.isError && !labour.query.isError;
  const candidatesReady =
    staffingReady &&
    can('fravaer.se') &&
    [usersQ, absQ, availQ].every((q) => q.data !== undefined && !q.isError);
  const q = sp.get('sok') ?? '';
  const seesQueue = can('timeliste.se');
  const queueLink = overviewLinks('dagen', sp).queue;
  const filter = (FILTERS.some(([k]) => k === sp.get('vis')) ? sp.get('vis') : 'alle') as Filter;
  const selectedId = Number(sp.get('valgt')) || 0;
  const set = (k: string, v: string | null, replace = true) => {
    const next = new URLSearchParams(sp);
    if (v == null || v === '') next.delete(k);
    else next.set(k, v);
    setSp(next, { replace });
  };

  // a person in several teams belongs to each of them; an open shift belongs to no team
  const teamOf = new Map(users.map((u) => [u.id, (u.teams ?? []).map((t) => t.id)]));
  const inTeam = (r: DayRow) =>
    !team || (r.shift.user_id != null && (teamOf.get(r.shift.user_id) ?? []).includes(team));
  const scoped = rows.filter(inTeam);
  const needle = q.trim().toLocaleLowerCase('nb');
  const shown = scoped.filter(
    (r) =>
      matches(r, filter) &&
      (!needle ||
        (r.shift.user_id == null ? T('Ledig vakt') : fullName(r.shift.user))
          .toLocaleLowerCase('nb')
          .includes(needle)),
  );
  const c = counts(scoped);
  const selected = selectedId ? rows.find((r) => r.shift.id === selectedId) : undefined;
  // links from the detail follow the shift's own date, not the overview's: a carry-over from yesterday opens yesterday (#274 R01)
  const selDay = (selected && oslo(selected.shift.start_time)?.date) || d;
  const teamName = (id: number) => (teamsQ.data ?? []).find((t) => t.id === id)?.name;
  const userTeams = (id: number | null) =>
    id == null
      ? ''
      : (users.find((u) => u.id === id)?.teams ?? [])
          .map((t) => t.name)
          .filter(Boolean)
          .join(', ');

  // the plan's totals for the rows in scope: planned shifts of this day only, a punch with no plan has no
  // planned hours and a shift carried over from yesterday counts on yesterday (#274 R02)
  const planned = scoped
    .filter((r) => r.shift.user_id != null && !r.unplanned && oslo(r.shift.start_time)?.date === d)
    .map((r) => r.shift);
  const est = estimate(planned, ratesOf(labour.rates), breakPolicy(tenant ?? undefined));
  const plannedPeople = new Set(planned.map((s) => s.user_id)).size;
  // shifts from yesterday still in the list, named under it so the totals and «N av M vist» reconcile (#274 N06)
  const carriedIn = scoped.filter(
    (r) => r.shift.user_id != null && !r.unplanned && (oslo(r.shift.start_time)?.date ?? d) < d,
  ).length;
  const seesCost = can('vaktplan.kost');

  const dayAll = dayShifts.filter((s) => oslo(s.start_time)?.date === d);
  // the chart and the gaps count every shift that reaches the day, a night shift from yesterday too
  // published shifts only, approved absence and planned lunch out (#273 chart v3.1, OV3-05); without the absence
  // read nothing is cut out, and the chart says so
  const absenceKnown =
    absQ.data !== undefined && !absQ.isError && absTypesQ.data !== undefined && !absTypesQ.isError;
  const work = staffWork(dayShifts, absenceKnown ? (absQ.data ?? []) : null, (a) =>
    kindOfAbsence(a, absTypesQ.data ?? []),
  );
  const gaps = rota ? hourGaps(labour.need, d, dayShifts, work) : [];
  const [openGap, setOpenGap] = useState<string | null>(null);
  const hasNeed = Object.keys(labour.need?.[dow(d)] ?? {}).length > 0;
  const free =
    rota && candidatesReady ? unscheduled(users, dayAll, absQ.data ?? [], availQ.data ?? [], d) : [];
  const freeList = freeShown(free, freeQ, freeAll);

  const tasks = dayAll
    .flatMap((sh) =>
      (sh.taskAssignments ?? [])
        .filter((a) => a.status !== 'completed' && a.id !== doneTask)
        .map((a) => ({ a, sh, due: taskDue(a, sh), start: taskStart(a, sh) })),
    )
    .sort((x, y) => x.start - y.start || x.due - y.due);
  const nowTasks = tasks.filter((t) => t.start <= now || t.due < now);
  const laterTasks = tasks.filter((t) => !(t.start <= now || t.due < now));

  const tomorrow = addDays(d, 1);
  const ahead = (absQ.data ?? [])
    .filter((a) => a.status === 'accepted' && (oslo(a.end_date)?.date ?? a.end_date) >= d)
    .filter((a) => (oslo(a.start_date)?.date ?? a.start_date) <= addDays(d, 13))
    .sort((a, b) => a.start_date.localeCompare(b.start_date));

  const hendelser = events(Array.isArray(historyQ.data) ? historyQ.data : [], dayShifts, users);

  const plannedText = (s: ApiShift, r: DayRow) =>
    r.unplanned || !rota ? null : shiftSpan(s.start_time, s.end_time);
  const actualText = (s: ApiShift) =>
    s.punch_in_time ? shiftSpan(s.punch_in_time, s.punch_out_time, T('åpen')) : null;
  // the shift type ("Dag", "Kveld"), the old Dagen's Vakt column; a punch with no plan has none
  const vaktText = (r: DayRow) => (r.unplanned || !rota ? '' : typeName(r.shift.shiftType, ''));
  // the address keeps where the detail was opened from, so a round trip through Back returns to that control (#274 R07)
  const openRow = (r: DayRow, from?: 'att') => {
    const next = new URLSearchParams(sp);
    next.set('valgt', String(r.shift.id));
    if (from) next.set('fra', from);
    else next.delete('fra');
    setSp(next);
  };
  const close = () => {
    const next = new URLSearchParams(sp);
    next.delete('valgt');
    next.delete('fra');
    setSp(next, { replace: true });
  };

  const exportCsv = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(
      new Blob(
        [
          csvOf([
            [
              T('Ansatt'),
              T('Vakt'),
              T('Planlagt'),
              T('Registrert'),
              T('Registrert pause (min)'),
              T('Status'),
            ],
            ...shown.map((r) => [
              r.shift.user_id == null ? T('Ledig vakt') : fullName(r.shift.user),
              vaktText(r),
              plannedText(r.shift, r) ?? '',
              actualText(r.shift) ?? '',
              r.pauseMin == null ? '' : String(r.pauseMin),
              r.label,
            ]),
          ]),
        ],
        { type: 'text/csv;charset=utf-8' },
      ),
    );
    a.download = `dagen-${d}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const complete = (id: number) => {
    setDoneTask(id);
    taskStatus
      .mutateAsync({ id, status: 'completed' })
      .then(() => {
        toast(T('Aktiviteten er fullført'));
        return invalidateShifts(qc);
      })
      .catch((e: Error) => {
        setDoneTask(null);
        toast(errorText(e));
      });
  };

  const drawerShift = draft?.id != null ? dayShifts.find((x) => x.id === draft.id) : undefined;
  const drawerUser = users.find((u) => u.id === drawerShift?.user_id);
  // correcting a punch is the planner's editor with the punch fields; the server checks each field against the role
  const mayCorrect = can('vaktplan.endre') || can('timeliste.rette');
  const firstGap = gaps[0];

  // what needs the manager first, for the whole company: each row to clarify and each staffing gap, named,
  // with its one action (#273 T02). The team filter is the roster's alone.
  const reviews = rows.filter((r) => r.review);
  const showGaps = rota && can('vaktplan.se') && staffingReady && hasNeed;
  // the named rows need only the shifts; a missing need read hides the gaps, not the people (#273 review S2-3)
  const needMissing = rota && can('vaktplan.se') && !staffingReady;
  const attentionKnown = shiftsQ.data !== undefined && !needMissing;
  const FIRST = 4;
  const reviewShown = reviews.slice(0, FIRST);
  // a gap opens its detail under the chart, from «Dette trenger deg», the chart or its own button (#273 OV3-04)
  const toGap = (i: number) => {
    const g = gaps[i];
    if (!g) return;
    setOpenGap(gapText(g));
    const el = document.getElementById(`ov-gap-${i}`);
    el?.scrollIntoView?.({ block: 'center' });
    el?.querySelector<HTMLElement>('.oxgapb')?.focus();
  };
  const attention = (
    <section className="oxs oxatt" aria-labelledby="ov-att" data-testid="ov-attention">
      <div className="oxsh">
        <div>
          <h2 id="ov-att" tabIndex={-1}>
            {T('Dette trenger deg')}
          </h2>
          <p className="oxsub">
            {dayTitle(d)}
            {/* people and stretches counted apart, so the lead, its rows and «Vis alle» agree (#274 N06) */}
            {attentionKnown && `, ${reviews.length} ${T('å avklare')}`}
            {attentionKnown && showGaps && gaps.length > 0 && `, ${gaps.length} ${T('tidsrom under behov')}`}
          </p>
        </div>
      </div>
      {shiftsQ.data === undefined ? (
        <ReadNotice
          reads={[
            [T('dagens vakter'), shiftsQ],
            ...(rota && can('vaktplan.se')
              ? [[T('bemanningsbehov'), labour.query] as [string, typeof labour.query]]
              : []),
          ]}
        />
      ) : reviews.length === 0 && !(showGaps && gaps.length) ? (
        <p className="oxclear" data-testid="ov-clear">
          <Icon name="check" />
          <span>
            <b>{T('Ingen vakter å avklare')}</b>
            <small>
              {showGaps
                ? T('Planen dekker også behovet hele dagen.')
                : !needMissing && rota && can('vaktplan.se') && !hasNeed
                  ? T('Bemanningsbehovet er ikke satt, så dekningen kan ikke vurderes.')
                  : T('Alle registreringer så langt stemmer med planen.')}
            </small>
          </span>
        </p>
      ) : (
        <ul className="oxxl">
          {reviewShown.map((r) => {
            const s = r.shift;
            const name = fullName(s.user);
            const p = plannedText(s, r);
            return (
              <li key={s.id} className="oxx" data-testid="ov-att-row">
                <span className="oxxi" aria-hidden="true">
                  <Icon name="clock" />
                </span>
                <span className="oxxt">
                  <b>
                    {r.presence === 'stale'
                      ? T('{name} er fortsatt stemplet inn', { name })
                      : T('{name} har ikke stemplet inn', { name })}
                  </b>
                  <small>
                    {[
                      p
                        ? `${(oslo(s.start_time)?.date ?? d) < d ? T('Vakt fra i går') : T('Planlagt')} ${p}`
                        : T('Ingen planlagt vakt'),
                      userTeams(s.user_id),
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </small>
                </span>
                <button
                  type="button"
                  className="oxgo"
                  data-att-open={s.id}
                  onClick={() => openRow(r, 'att')}
                  aria-label={`${T('Avklar')}: ${name}, ${T(r.label)}`}
                >
                  {T('Avklar')}
                  <Icon name="chevronRight" />
                </button>
              </li>
            );
          })}
          {showGaps &&
            gaps.map((g, i) => (
              <li key={`${g.from}-${g.to}`} className="oxx" data-testid="ov-att-gap">
                <span className="oxxi" aria-hidden="true">
                  <Icon name="users" />
                </span>
                <span className="oxxt">
                  <b>
                    {T('Under behov')} <span className="oxnw">{gapText(g)}</span>
                  </b>
                  <small>
                    <span className="oxwarn">{T('{n} av {of} planlagt', { n: g.have, of: g.need })}</span>,{' '}
                    {T('hele virksomheten')}
                  </small>
                </span>
                <button
                  type="button"
                  className="oxgo"
                  onClick={() => toGap(i)}
                  aria-label={`${T('Se dekning')} ${gapText(g)}`}
                >
                  {T('Se dekning')}
                  <Icon name="chevronRight" />
                </button>
              </li>
            ))}
        </ul>
      )}
      {shiftsQ.data !== undefined && needMissing && (
        <ReadNotice reads={[[T('bemanningsbehov'), labour.query]]} />
      )}
      {(reviews.length > reviewShown.length || seesQueue) && (
        <div className="oxsf">
          {reviews.length > reviewShown.length ? (
            <button
              type="button"
              className="mlink"
              onClick={() => {
                const next = new URLSearchParams(sp);
                next.set('vis', 'avklar');
                next.delete('lag');
                next.delete('sok');
                setSp(next, { replace: true });
                document.getElementById('ov-team')?.focus();
              }}
            >
              {T('Vis alle {n} i listen', { n: reviews.length })}
            </button>
          ) : (
            <span>{T('Se også saker fra andre dager')}</span>
          )}
          {seesQueue && (
            <Link className="mlink" to={queueLink}>
              {T('Oppfølging')}
              <Icon name="chevronRight" />
            </Link>
          )}
        </div>
      )}
    </section>
  );

  const list = (
    <section className="oxs oxteamlist" aria-labelledby="ov-team">
      <div className="oxsh">
        <div>
          <h2 id="ov-team" tabIndex={-1}>
            {T('Dagens team')}
          </h2>
          <p className="oxsub oxcount" data-testid="ov-counts">
            <span className={listKnown ? 'oxdot' : undefined}>
              {!listKnown ? NO_VALUE : plural(c.present, 'på jobb', 'på jobb')}
              {c.onBreak > 0 && `, ${c.onBreak} ${T('på pause')}`}
            </span>
            {rota && listKnown && (
              <span data-testid="ov-later">
                {c.later > 0
                  ? `${plural(c.later, 'kommer senere', 'kommer senere')}, ${T('neste')} ${c.next}`
                  : T('ingen flere i dag')}
              </span>
            )}
            {listKnown && c.review > 0 && (
              <span className="oxwarn">{plural(c.review, 'må avklares', 'må avklares')}</span>
            )}
            {listKnown && rota && planned.length > 0 && (
              <span>
                {num(est.hours, 1)} {T('t planlagt')}
              </span>
            )}
          </p>
        </div>
        {rota && can('vaktplan.se') && (
          <Link className="mlink" to={toVaktplan({ day: d })}>
            <Icon name="calendar" />
            {T('Åpne dagsplan')}
          </Link>
        )}
      </div>
      <div className="oxfilters">
        <label className="oxsearch">
          <Icon name="search" />
          <input
            className="inp"
            type="search"
            value={q}
            onChange={(e) => set('sok', e.target.value || null)}
            placeholder={T('Finn en ansatt')}
            aria-label={T('Finn en ansatt')}
          />
        </label>
        {can('vaktplan.se') && (
          <label className="oxf">
            <span>{T('Team i listen')}</span>
            <select className="inp" value={team || ''} onChange={(e) => set('lag', e.target.value || null)}>
              <option value="">{T('Alle team')}</option>
              {team && !teamName(team) ? (
                <option value={team}>
                  {T('Valgt team')} #{team}
                </option>
              ) : null}
              {(teamsQ.data ?? [])
                .filter((t) => t.name)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </select>
          </label>
        )}
        <label className="oxf">
          <span>{T('Oppmøte')}</span>
          <select
            className="inp"
            value={filter}
            onChange={(e) => set('vis', e.target.value === 'alle' ? null : e.target.value)}
            aria-label={T('Vis oppmøte')}
            data-testid="ov-filter"
          >
            {FILTERS.filter(([k]) => rota || (k !== 'later' && k !== 'open')).map(([k, label]) => (
              <option key={k} value={k}>
                {T(label)}
              </option>
            ))}
          </select>
        </label>
        {listKnown && (
          <span className="oxshown" data-testid="ov-shown">
            {T('{n} av {of} vist', { n: shown.length, of: scoped.length })}
          </span>
        )}
      </div>
      <ReadNotice
        reads={[
          [T('dagens vakter'), shiftsQ],
          ...(team ? [[T('teamtilhørighet'), usersQ] as [string, typeof usersQ]] : []),
          ...(can('vaktplan.se') ? [[T('team'), teamsQ] as [string, typeof teamsQ]] : []),
        ]}
      />
      {!listKnown ? (
        shiftsQ.isError || (team && usersQ.isError) ? null : (
          <div className="oxpad">
            <Skeleton rows={4} />
          </div>
        )
      ) : shown.length === 0 ? (
        <Empty>
          {team && rows.length > 0 && scoped.length === 0
            ? T('Ingen vakter i dette teamet denne dagen.')
            : scoped.length === 0
              ? rota
                ? T('Ingen vakter eller registreringer denne dagen.')
                : T('Ingen registreringer denne dagen ennå.')
              : T('Ingen i dette utvalget.')}{' '}
          {scoped.length > 0 && (
            <button
              type="button"
              className="mlink"
              onClick={() => {
                const next = new URLSearchParams(sp);
                next.delete('vis');
                next.delete('sok');
                setSp(next, { replace: true });
                // the button goes with the empty result; the search box keeps the keyboard in the list (#273 S2-4)
                document.querySelector<HTMLInputElement>('.ox .oxsearch input')?.focus();
              }}
            >
              {T('Nullstill filter')}
            </button>
          )}
        </Empty>
      ) : (
        <table className="mt oxt" data-testid="ov-list">
          <thead>
            <tr>
              <th>{T('Ansatt')}</th>
              {rota && <th>{T('Planlagt')}</th>}
              <th>{T('Registrert')}</th>
              <th>{T('Status')}</th>
              <th className="oxact">{T('Handling')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const s = r.shift;
              const p = plannedText(s, r);
              const a = actualText(s);
              const left = tasksLeft(s);
              const carried = (oslo(s.start_time)?.date ?? d) < d;
              const name = s.user_id == null ? T('Ledig vakt') : fullName(s.user);
              const who = [s.user_id == null ? '' : userTeams(s.user_id), vaktText(r)]
                .filter(Boolean)
                .join(', ');
              return (
                <tr key={s.id} className={selectedId === s.id ? 'sel' : ''} data-testid="ov-row">
                  <td className="oxwho">
                    <span className="mwho">
                      <span className={`av ${avTone(s.user_id)}`} aria-hidden="true">
                        {s.user_id == null ? NO_VALUE : initialsOf(name)}
                      </span>
                      <span>
                        <b>{name}</b>
                        {who && <small>{who}</small>}
                      </span>
                    </span>
                  </td>
                  {rota && (
                    <td className="oxplan" data-k={T('Planlagt')}>
                      {p ? (
                        <>
                          <span className="tnum">{p}</span>
                          <small className={s.published || carried ? '' : 'oxdraft'}>
                            {carried
                              ? T('Vakt fra i går')
                              : s.published
                                ? T('Publisert')
                                : T('Utkast, ikke publisert')}
                          </small>
                        </>
                      ) : (
                        <span className="oxmute">{T('Ingen planlagt vakt')}</span>
                      )}
                    </td>
                  )}
                  <td className="oxreg" data-k={T('Registrert')}>
                    {a ? (
                      <>
                        <span className="tnum">{a}</span>
                        <small>
                          {showsPause(r.pauseMin, s.break_mode ?? breakMode)
                            ? `${r.pauseMin ?? 0} ${T('min registrert pause')}`
                            : s.punch_out_time
                              ? T('Utregistrert')
                              : T('Innregistrert')}
                        </small>
                      </>
                    ) : (
                      <>
                        <span className="tnum" aria-hidden="true">
                          {NO_VALUE}
                        </span>
                        <small>{s.user_id == null ? T('Ingen tildelt') : T('Ingen registrering')}</small>
                      </>
                    )}
                  </td>
                  <td className="oxstat" data-k={T('Status')}>
                    <Status tone={r.tone}>{T(r.label)}</Status>
                    {left != null && left > 0 && (
                      <small>{plural(left, 'aktivitet gjenstår', 'aktiviteter gjenstår')}</small>
                    )}
                  </td>
                  <td className="oxact">
                    {s.user_id == null && can('vaktplan.endre') ? (
                      <button
                        type="button"
                        className="oxgo"
                        onClick={() => {
                          setOnPunch(false);
                          setDraft(draftOf(s));
                        }}
                        aria-label={T('Tildel ledig vakt {from} til {to}', {
                          from: clock(s.start_time),
                          to: clock(s.end_time),
                        })}
                      >
                        {T('Tildel')}
                        <Icon name="chevronRight" />
                      </button>
                    ) : r.review ? (
                      <button
                        type="button"
                        className="oxgo"
                        onClick={() => openRow(r)}
                        data-day-open={s.id}
                        aria-label={`${T('Avklar')}: ${name}, ${T(r.label)}`}
                      >
                        {T('Avklar')}
                        <Icon name="chevronRight" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="oxgo"
                        onClick={() => openRow(r)}
                        data-day-open={s.id}
                        aria-label={`${T('Se vakt')}: ${name}, ${T(r.label)}`}
                      >
                        {T('Se vakt')}
                        <Icon name="chevronRight" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {listKnown && (
        <div className="oxsf">
          {rota ? (
            <span data-testid="ov-sum">
              {team ? (teamName(team) ?? T('Valgt team')) : T('Hele dagen')}:{' '}
              {plural(planned.length, 'vakt', 'vakter')}, {plural(plannedPeople, 'person', 'personer')},{' '}
              {num(est.hours, 1)} {T('t planlagt')}
              {seesCost &&
                showCost &&
                (labour.ready
                  ? `, ${kr(est.cost)} kr ${T('estimert')}`
                  : `, ${T('Kostnad ikke tilgjengelig')}`)}
              {carriedIn > 0 &&
                `. ${carriedIn === 1 ? T('1 vakt fra i går er ikke medregnet') : T('{n} vakter fra i går er ikke medregnet', { n: carriedIn })}`}
            </span>
          ) : (
            <span>{plural(scoped.length, 'registrering', 'registreringer')}</span>
          )}
          {rota && seesCost && (
            <button
              type="button"
              className="mlink"
              onClick={() => setShowCost((x) => !x)}
              aria-pressed={showCost}
            >
              {showCost ? T('Skjul kostnad') : T('Vis kostnad')}
            </button>
          )}
          <span className="sp" />
          {/* only with rows to export: disabled while loading it was the one control shown, at 2.66:1 (#272 area A colour) */}
          {shown.length > 0 && (
            <Button small onClick={exportCsv}>
              <Icon name="download" />
              {T('Eksporter vist liste')}
            </Button>
          )}
        </div>
      )}
    </section>
  );

  // company-wide, whatever the roster's filters (#273 T03): planned per hour against the need, the gaps in words
  const drafts = dayAll.filter((s) => !s.published).length;
  const staffing = rota && can('vaktplan.se') && (
    <section className="oxs" id="ov-staff" aria-labelledby="ov-staff-h" data-testid="ov-staff">
      <div className="oxsh">
        <div>
          <h2 id="ov-staff-h" tabIndex={-1}>
            {T('Bemanning gjennom dagen')}
          </h2>
          <p className="oxsub">{T('Antall personer, time for time')}</p>
        </div>
        <span className="oxscope" title={T('Hele virksomheten, alle team')}>
          <Icon name="users" />
          {T('Alle team')}
        </span>
      </div>
      <ReadNotice
        reads={[
          [T('dagens vakter'), shiftsQ],
          [T('bemanningsbehov'), labour.query],
        ]}
      />
      {staffingKnown && (
        <Coverage
          shifts={dayShifts}
          d={d}
          need={labour.need}
          work={work}
          gaps={hasNeed && staffingReady ? gaps : []}
          absenceKnown={absenceKnown}
          openingHours={tenant?.opening_hours}
          current={staffingReady}
          now={d === today ? now : null}
          onPick={toGap}
        />
      )}
      {staffingKnown && (
        <div className="oxgapl" data-testid="ov-gaps">
          {!staffingReady ? (
            <p className="oxmute">{T('Venter på oppdaterte vakter og bemanningsbehov.')}</p>
          ) : !hasNeed ? (
            <p>
              {T('Behovet er ikke satt for denne dagen.')}{' '}
              {can('innstillinger.se') && (
                <Link className="mlink" to="/innstillinger/bemanning">
                  <Icon name="sliders" />
                  {T('Sett behov')}
                </Link>
              )}
            </p>
          ) : gaps.length === 0 ? (
            <p>{T('Planen dekker behovet hele dagen.')}</p>
          ) : (
            <>
              <p className="oxwarn oxgaph">
                <Icon name="warn" />
                {T('{n} tidsrom under behov', { n: gaps.length })}
              </p>
              {gaps.map((g, i) => {
                // an open shift already in the gap is filled before another is made (#273 review S2-1)
                const [a, b] = gapSpan(d, g);
                const open = dayShifts.find(
                  (x) => x.user_id == null && Date.parse(x.start_time) < b && Date.parse(x.end_time) > a,
                );
                const key = gapText(g);
                const shown = openGap === key;
                return (
                  <div className="oxgap" key={key} id={`ov-gap-${i}`}>
                    <div className="oxgapr">
                      <button
                        type="button"
                        className="oxgapb"
                        aria-expanded={shown}
                        aria-controls={`ov-gapd-${i}`}
                        onClick={() => setOpenGap(shown ? null : key)}
                      >
                        <b className="tnum">{key}</b>
                        {/* one figure format, «1 av 2», amber because every row here is under the need (#272 A-10) */}
                        <small className="oxwarn" data-testid="ov-gap-count">
                          {T('{n} av {of}', { n: g.have, of: g.need })} {T('planlagt mot behov')}
                        </small>
                        <Icon name="chevronDown" />
                      </button>
                      {can('vaktplan.endre') && open ? (
                        <button
                          type="button"
                          className="oxgo"
                          data-testid="ov-gap-fill"
                          onClick={() => {
                            setOnPunch(false);
                            setDraft(draftOf(open));
                          }}
                          aria-label={T('Tildel ledig vakt {from} til {to}', {
                            from: oslo(open.start_time)?.time ?? '',
                            to: oslo(open.end_time)?.time ?? '',
                          })}
                        >
                          {T('Tildel ledig vakt')}
                          <Icon name="chevronRight" />
                        </button>
                      ) : can('vaktplan.endre') ? (
                        <Link
                          className="oxgo"
                          to={toVaktplan({ day: d, newShift: { from: g.from, to: g.to } })}
                          aria-label={`${T('Opprett ledig vakt')} ${key}`}
                        >
                          {T('Opprett ledig vakt')}
                          <Icon name="chevronRight" />
                        </Link>
                      ) : (
                        <Link className="mlink" to={toVaktplan({ day: d })}>
                          {T('Åpne dagsplan')}
                        </Link>
                      )}
                    </div>
                    {shown && (
                      <GapDetail
                        id={`ov-gapd-${i}`}
                        g={g}
                        span={[a, b]}
                        work={work}
                        shifts={dayShifts}
                        absences={absenceKnown ? (absQ.data ?? []) : null}
                        kindOf={(x) => kindOfAbsence(x, absTypesQ.data ?? [])}
                        teams={(teamsQ.data ?? []).filter((t) => t.name)}
                        users={users}
                        onClose={() => {
                          setOpenGap(null);
                          document.querySelector<HTMLElement>(`#ov-gap-${i} .oxgapb`)?.focus();
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </>
          )}
          {drafts > 0 && (
            // a draft is not cover until it is published, and making one tells nobody (#273 OV3-05)
            <p className="oxmute oxdraftnote" data-testid="ov-drafts">
              {T('{n} er ikke publisert og teller ikke som dekning. Ingen er varslet.', {
                n: plural(drafts, 'utkast', 'utkast'),
              })}
            </p>
          )}
        </div>
      )}
      {shiftsQ.data && (
        <RecordedVsPlanned
          shifts={dayShifts}
          d={d}
          now={now}
          isToday={d === today}
          openingHours={tenant?.opening_hours}
        />
      )}
      <details className="mexp oxfree" data-testid="ov-free">
        <summary>
          {T('Finn ekstra bemanning')} {candidatesReady ? `(${free.length})` : ''}
        </summary>
        <div className="mexb">
          <p className="oxmute">
            {T(
              'Ikke satt opp denne dagen, ikke borte og ikke meldt utilgjengelig. Ikke oppgitt tilgjengelighet betyr ikke at personen kan.',
            )}
          </p>
          {!can('fravaer.se') ? (
            <p>{T('Du trenger tilgang til fravær for å se bemanningsforslag.')}</p>
          ) : (
            <ReadNotice
              reads={[
                [T('dagens vakter'), shiftsQ],
                [T('ansatte'), usersQ],
                [T('fravær'), absQ],
                [T('tilgjengelighet'), availQ],
                [T('bemanningsbehov'), labour.query],
              ]}
            />
          )}
          {candidatesReady && free.length > 0 && (
            <input
              className="inp"
              type="search"
              value={freeQ}
              onChange={(e) => setFreeQ(e.target.value)}
              placeholder={T('Søk etter ansatt …')}
              aria-label={T('Søk etter ansatt')}
            />
          )}
          {!candidatesReady ? null : freeList.length === 0 ? (
            <p className="oxmute">{freeQ.trim() ? T('Ingen treff.') : T('Ingen flere å foreslå.')}</p>
          ) : (
            <ul>
              {freeList.map((u) => {
                const av = availOn(availQ.data ?? [], u.id, d);
                return (
                  <li key={u.id}>
                    <span className="mwho">
                      <span className={`av ${avTone(u.id)}`}>{initialsOf(fullName(u))}</span>
                      <span>
                        <b>{fullName(u)}</b>
                        <small>
                          {av?.kind === 'partly'
                            ? T('Delvis tilgjengelig {from}–{to}', {
                                from: av.from,
                                to: av.to,
                              })
                            : T('Tilgjengelighet ikke oppgitt')}
                          {u.max_hours_per_week != null ? (
                            `, ${num(u.max_hours_per_week)} ${T('t avtalt per uke')}`
                          ) : can('ansatte.endre') ? (
                            // the agreed week reaches those who manage people only (models/user.js);
                            // for anyone else it is absent, and 37,5 would state a contract that may not be theirs
                            <>
                              {', '}
                              <span title={T('Ikke oppgitt, standard brukt')}>
                                37,5* {T('t avtalt per uke')}
                              </span>
                            </>
                          ) : (
                            ''
                          )}
                        </small>
                      </span>
                    </span>
                    {can('vaktplan.endre') && (
                      <Link
                        className="mlink"
                        to={
                          firstGap
                            ? toVaktplan({
                                day: d,
                                newShift: {
                                  from: firstGap.from,
                                  to: firstGap.to,
                                  userId: u.id,
                                },
                              })
                            : toVaktplan({ day: d })
                        }
                        aria-label={`${T('Sett opp')} ${fullName(u)}${firstGap ? ` ${gapText(firstGap)}` : ''}`}
                      >
                        {T('Sett opp')}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {!freeQ.trim() && free.length > freeList.length && (
            <button type="button" className="mlink" onClick={() => setFreeAll(true)}>
              {T('Vis alle {n}', { n: free.length })}
            </button>
          )}
        </div>
      </details>
    </section>
  );

  // the activities live on the shifts: «0 gjenstår» before they answer would be invented too (#272 A-01)
  const activities = shiftsQ.data && (rota || tasks.length > 0) && (
    <section className="oxs" aria-labelledby="ov-tasks" data-testid="ov-tasks">
      <div className="oxsh">
        <div>
          <h2 id="ov-tasks">{T('Aktiviteter nå')}</h2>
          {tasks.length > 0 && (
            <p className="oxsub">
              {tasks.length} {T('gjenstår')}
            </p>
          )}
        </div>
      </div>
      {nowTasks.length === 0 && laterTasks.length === 0 ? (
        <p className="oxpad oxmute oxnone">{T('Ingen åpne aktiviteter denne dagen.')}</p>
      ) : (
        <ul className="oxtasks">
          {(nowTasks.length ? nowTasks : []).map(({ a, sh, due }) => (
            <TaskRow
              key={a.id}
              a={a}
              sh={sh}
              overdue={due < now}
              done={doneTask === a.id}
              onDone={complete}
            />
          ))}
          {nowTasks.length === 0 && <li className="oxmute">{T('Ingen aktiviteter akkurat nå.')}</li>}
        </ul>
      )}
      {laterTasks.length > 0 && (
        <div className="oxsf">
          <button
            type="button"
            className="mlink"
            aria-expanded={laterOpen}
            onClick={() => setLaterOpen((x) => !x)}
          >
            {T('Senere i dag')}, {plural(laterTasks.length, 'aktivitet', 'aktiviteter')}
          </button>
        </div>
      )}
      {laterOpen && (
        <ul className="oxtasks">
          {laterTasks.map(({ a, sh }) => (
            <TaskRow key={a.id} a={a} sh={sh} overdue={false} done={doneTask === a.id} onDone={complete} />
          ))}
        </ul>
      )}
    </section>
  );

  return (
    <div className="oxday">
      {rota && <FirstPlanHint on="hjem" />}
      <div className={`msplit${selected ? ' open' : ''}`}>
        <div className="oxmain">
          <div className={staffing ? 'oxlead' : 'oxlead one'}>
            {attention}
            {staffing}
          </div>
          {list}
          {activities}
          <Explain q={T('Neste dag og kommende fravær')} className="oxnext">
            <p>
              <Link to={`/hjem?d=${tomorrow}`}>
                {T('Se {day}', { day: dayTitle(tomorrow) })}
                <Icon name="chevronRight" />
              </Link>
            </p>
            {can('fravaer.se') &&
              (absQ.data === undefined || absQ.isError ? (
                <ReadNotice reads={[[T('kommende fravær'), absQ]]} />
              ) : ahead.length ? (
                <ul className="oxabs">
                  {ahead.slice(0, 8).map((a) => (
                    <li key={a.id}>
                      <Link to={`/fravaer/soknader?apne=${a.id}`}>
                        <b>{a.user ? fullName(a.user) : T('Ukjent')}</b>,{' '}
                        {typeName(a.absence_type, T('Fravær'))}
                      </Link>
                      <span className="tnum">
                        {fmtDs(oslo(a.start_date)?.date ?? a.start_date)}
                        {a.end_date !== a.start_date
                          ? ` – ${fmtDs(oslo(a.end_date)?.date ?? a.end_date)}`
                          : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="oxmute">{T('Ingen godkjent fravær de neste 14 dagene.')}</p>
              ))}
          </Explain>
          <div className="oxsec">
            {seesHistory && (
              <Explain q={T('Endringshistorikk')} className="oxhist">
                {historyQ.isError ? (
                  <ReadNotice reads={[[T('endringshistorikk'), historyQ]]} />
                ) : historyQ.isPending ? (
                  <Skeleton rows={2} />
                ) : hendelser.length ? (
                  <ol className="oxevents">
                    {hendelser.slice(0, 12).map((e) => (
                      <li key={e.id}>
                        <span className="tnum">{clock(e.at)}</span> {e.text}
                        {e.note && <small>, {e.note}</small>}
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="oxmute">{T('Ingen stemplinger registrert denne dagen ennå.')}</p>
                )}
                <Link to={`/arbeidstid?periode=dag&dato=${d}`}>
                  {T('Se alle registreringer')}
                  <Icon name="chevronRight" />
                </Link>
              </Explain>
            )}
          </div>
        </div>
        {selected && (
          <DetailPanel
            restoreFocusTo={`[data-${sp.get('fra') === 'att' ? 'att' : 'day'}-open="${selected.shift.id}"]`}
            title={
              selected.shift.user_id == null ? (
                T('Ledig vakt')
              ) : (
                <PersonLabel id={selected.shift.user_id} name={fullName(selected.shift.user)} />
              )
            }
            sub={`${dayTitle(selDay)}, ${T(selected.label)}`}
            onClose={close}
            label={T('Detaljer')}
            footer={
              <>
                {/* Lukk first, the next step last (UI consistency v1 CP-10: cancel or secondary, then primary) */}
                <Button ghost onClick={close}>
                  {T('Lukk')}
                </Button>
                {rota && selected.shift.user_id != null && (
                  <Link className="btn" to={toVaktplan({ day: selDay })}>
                    <Icon name="calendar" />
                    {T('Åpne dagsplan')}
                  </Link>
                )}
                {/* any staffed shift opens in the editor, as the row's «Åpne vakten» did (#273 OV-15) */}
                {selected.shift.user_id != null && mayCorrect && (
                  <Button
                    tone="pri"
                    onClick={() => {
                      setOnPunch(selected.review || selected.shift.punch_in_time != null);
                      setDraft(draftOf(selected.shift));
                    }}
                  >
                    {selected.review || selected.shift.punch_in_time != null
                      ? T('Rett stempling')
                      : T('Åpne vakten')}
                  </Button>
                )}
                {selected.shift.user_id == null && can('vaktplan.endre') && (
                  <Link className="btn pri" to={toVaktplan({ day: selDay })}>
                    <Icon name="users" />
                    {T('Finn noen i dagsplanen')}
                  </Link>
                )}
              </>
            }
          >
            {/* the facts first, before any action: what was planned and what was recorded (#273 T06) */}
            <div className="oxfacts" data-testid="ov-facts">
              {rota && (
                <div>
                  <span>{T('Planlagt vakt')}</span>
                  <b className="tnum">
                    {selected.unplanned
                      ? T('Ingen')
                      : `${clock(selected.shift.start_time)}–${clock(selected.shift.end_time)}`}
                  </b>
                </div>
              )}
              <div>
                <span>{T('Registrert')}</span>
                <b className="tnum">{actualText(selected.shift) ?? T('Ingen stempling')}</b>
              </div>
            </div>
            {selected.review && <p className="oxexplain">{T(selected.explain)}</p>}
            {rota && !selected.unplanned && selected.shift.planned_lunch_start && (
              <Fact k={T('Planlagt lunsj')}>
                {`${clock(selected.shift.planned_lunch_start)}, ${selected.shift.planned_lunch_minutes ?? 0} min`}
              </Fact>
            )}
            <Fact k={T('Registrert inn')}>
              {selected.shift.punch_in_time ? clock(selected.shift.punch_in_time) : T('Ikke registrert')}
            </Fact>
            <Fact k={T('Registrert ut')}>
              {selected.shift.punch_out_time ? clock(selected.shift.punch_out_time) : T('Ikke registrert')}
            </Fact>
            <Fact k={T('Registrert pause')}>
              {selected.pauseMin == null ? NO_VALUE : `${selected.pauseMin} min`}
            </Fact>
            {selected.shift.punch_in_time && selected.shift.punch_out_time && (
              <Fact k={T('Registrert tid')}>{`${num(hours(selected.shift, null), 2)} t`}</Fact>
            )}
            {(selected.shift.taskAssignments ?? []).length > 0 && (
              <div className="oxptasks">
                <b>{T('Aktiviteter')}</b>
                <ul>
                  {(selected.shift.taskAssignments ?? []).map((a) => (
                    <li key={a.id}>
                      {a.shiftTask?.name ?? T('Aktivitet')}{' '}
                      <small>
                        {a.status === 'completed'
                          ? `${T('fullført')}${a.completed_at ? ` ${clock(a.completed_at)}` : ''}${a.completedByUser ? ` ${T('av')} ${fullName(a.completedByUser)}` : ''}`
                          : T('ikke fullført')}
                      </small>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="oxplinks">
              {selected.shift.user_id != null && can('ansatte.se') && (
                <Link to={`/ansatte/${selected.shift.user_id}`}>
                  {T('Åpne ansattprofil')}
                  <Icon name="chevronRight" />
                </Link>
              )}
              {selected.shift.user_id != null && can('timeliste.se') && (
                <Link to={`/arbeidstid?periode=dag&dato=${selDay}&ansatt=${selected.shift.user_id}`}>
                  {T('Se registreringer i Kontroll')}
                  <Icon name="chevronRight" />
                </Link>
              )}
              {team > 0 && <span className="oxmute">{teamName(team)}</span>}
            </div>
          </DetailPanel>
        )}
      </div>

      <ShiftDrawer
        draft={draft}
        focus={
          draft?.punchIn && !draft.punchOut
            ? { punch: 'out' }
            : onPunch
              ? { section: 'stempling' }
              : undefined
        }
        tasks={drawerShift?.taskAssignments}
        entries={
          drawerShift && drawerUser && tenant
            ? dayEntries(drawerShift, rulePerson(drawerUser, tenant), tenantRules(tenant))
            : []
        }
        types={types}
        users={users.filter((u) => u.active_status)}
        onClose={() => setDraft(null)}
        // the editor's save and its delete cross the same seam as every shift write in Vaktplan
        // (lock.ts): a double click sends one request, an unknown outcome locks the shift
        submit={(id, f) =>
          lock.op([id], T('Lagret'), async () => {
            await f();
            return undefined;
          })
        }
        locked={draft?.id != null && lock.busy(draft.id)}
        onReread={() => void lock.reread()}
        onDelete={async (id, reason) => {
          const ok = await lock.op([id], T('Vakten er slettet'), async () => {
            await w.remove.mutateAsync({ id, ...(reason ? { reason } : {}) });
            return undefined;
          });
          if (ok) {
            setDraft(null);
            close();
          }
        }}
      />
    </div>
  );
}

/**
 * One gap's exact figures (#273 OV3-04, OV3-05): the people at once against the need for the whole company,
 * each team's people at once in the same stretch (the need is set for the company, not per team), the drafts
 * that reach it and do not count, and who is away. Lukk returns focus to the gap's button.
 */
function GapDetail({
  id,
  g,
  span: [a, b],
  work,
  shifts,
  absences,
  kindOf,
  teams,
  users,
  onClose,
}: {
  id: string;
  g: HourGap;
  span: [number, number];
  work: ReturnType<typeof staffWork>;
  shifts: ApiShift[];
  absences: ApiAbsence[] | null;
  kindOf: (a: ApiAbsence) => Kind;
  teams: { id: number; name: string }[];
  users: ApiUser[];
  onClose: () => void;
}) {
  const teamOf = new Map(users.map((u) => [u.id, (u.teams ?? []).map((t) => t.id)]));
  const least = (w: typeof work) => Math.min(...pieces(w, a, b).map((p) => p.have));
  const byTeam = teams.map((t) => ({
    t,
    n: least(work.filter((w) => (teamOf.get(w.u) ?? []).includes(t.id))),
  }));
  const drafts = shifts.filter(
    (s) => !s.published && Date.parse(s.start_time) < b && Date.parse(s.end_time) > a,
  );
  const away =
    absences?.filter((x) => {
      const sp = absenceSpan(x, kindOf(x));
      return (
        x.status === 'accepted' &&
        sp &&
        sp[0] < b &&
        sp[1] > a &&
        shifts.some(
          (s) => s.user_id === x.user_id && Date.parse(s.start_time) < b && Date.parse(s.end_time) > a,
        )
      );
    }) ?? [];
  return (
    <div className="oxgapd" id={id} data-testid="ov-gap-detail">
      <dl>
        <div>
          <dt>{T('Hele virksomheten')}</dt>
          <dd className="tnum">{T('{n} av {of} planlagt', { n: g.have, of: g.need })}</dd>
        </div>
        {byTeam.map(({ t, n }) => (
          <div key={t.id}>
            <dt>{t.name}</dt>
            <dd className="tnum">{T('{n} planlagt', { n })}</dd>
          </div>
        ))}
      </dl>
      <p className="oxmute">
        {T(
          'Behovet er satt for hele virksomheten, ikke per team. Tallene er det minste antallet samtidig i tidsrommet.',
        )}
      </p>
      {drafts.length > 0 && (
        <p data-testid="ov-gap-drafts">
          {T('{n} i tidsrommet er ikke publisert og teller ikke. Ingen er varslet.', {
            n: plural(drafts.length, 'utkast', 'utkast'),
          })}
        </p>
      )}
      {away.length > 0 && (
        <p>
          {T('Godkjent fravær')}: {away.map((x) => (x.user ? fullName(x.user) : T('Ukjent'))).join(', ')}
        </p>
      )}
      {absences == null && <p className="oxmute">{T('Fravær kunne ikke leses og er ikke trukket fra.')}</p>}
      <Button small onClick={onClose}>
        {T('Lukk')}
      </Button>
    </div>
  );
}

/** When an activity starts: its own clock, else the shift's start. */
function taskStart(a: { has_time_range?: boolean; start_time?: string | null }, sh: ApiShift): number {
  if (a.has_time_range && a.start_time) {
    if (/^\d{2}:\d{2}/.test(a.start_time)) {
      const day = oslo(sh.start_time)?.date;
      return day ? Date.parse(osloISO(day, a.start_time.slice(0, 5))) : Date.parse(sh.start_time);
    }
    const t = Date.parse(a.start_time);
    if (Number.isFinite(t)) return t;
  }
  return Date.parse(sh.start_time);
}

function TaskRow({
  a,
  sh,
  overdue,
  done,
  onDone,
}: {
  a: NonNullable<ApiShift['taskAssignments']>[number];
  sh: ApiShift;
  overdue: boolean;
  done: boolean;
  onDone: (id: number) => void;
}) {
  const when =
    a.has_time_range && a.start_time
      ? `${hhmm(a.start_time)}${a.end_time ? `–${hhmm(a.end_time)}` : ''}`
      : `${clock(sh.start_time)}–${clock(sh.end_time)}`;
  return (
    <li>
      <span>
        <b>{a.shiftTask?.name ?? T('Aktivitet')}</b>
        <small>
          {sh.user ? fullName(sh.user) : T('Ledig vakt')}, {when}
          {overdue ? `, ${T('ikke fullført')}` : ''}
        </small>
      </span>
      <Button small onClick={() => onDone(a.id)} disabled={done}>
        {done ? T('Fullfører…') : T('Fullfør')}
      </Button>
    </li>
  );
}

/** Planned people against recorded people per hour, the recorded side only up to now (Hjem's comparison, kept). */
function RecordedVsPlanned({
  shifts,
  d,
  now,
  isToday,
  openingHours,
}: {
  shifts: ApiShift[];
  d: string;
  now: number;
  isToday: boolean;
  openingHours: Parameters<typeof span>[2];
}) {
  const [from, to] = span(shifts, d, openingHours);
  const planned = perHour(shifts, d).filter((b) => b.hour >= from && b.hour < to);
  const startOf = (h: number) => Date.parse(osloISO(d, `${String(h).padStart(2, '0')}:00`));
  const recorded = (h: number) => {
    const a = startOf(h);
    const b = a + 3_600_000;
    return new Set(
      shifts
        .filter((s) => s.user_id != null && s.punch_in_time)
        .filter((s) => {
          const i = Date.parse(s.punch_in_time as string);
          const o = s.punch_out_time ? Date.parse(s.punch_out_time) : now;
          return i < b && o > a;
        })
        .map((s) => s.user_id),
    ).size;
  };
  return (
    <details className="mexp oxrec">
      <summary>{T('Planlagt mot registrert, time for time')}</summary>
      <div className="mexb">
        <p>
          {T(
            'Planlagt teller personer med vakt i timen. Registrert teller personer stemplet inn i timen, og bare fram til nå.',
          )}
        </p>
        <table className="mt oxhours">
          <caption className="sr">{T('Planlagt mot registrert')}</caption>
          <thead>
            <tr>
              <th>{T('Time')}</th>
              <th className="num">{T('Planlagt')}</th>
              <th className="num">{T('Registrert')}</th>
            </tr>
          </thead>
          <tbody>
            {planned.map((b, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: the autumn DST day has two 02 hours
              <tr key={i}>
                <td className="tnum">{String(b.hour).padStart(2, '0')}:00</td>
                <td className="num">{b.count}</td>
                <td className="num">{isToday && startOf(b.hour) > now ? NO_VALUE : recorded(b.hour)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
